import { maxChatMessages } from "../../config/config.js";
import type {
  ChatHistoryMessage,
  ChatMessageDto,
  ChatMessageView,
  ChatSocketContent,
  ChatSocketPayload,
} from "../types";

export function capMessages<T>(items: T[], limit: number = maxChatMessages): T[] {
  if (items.length <= limit) return items;
  return items.slice(items.length - limit);
}

export function resolveUserId(payload: ChatSocketPayload): string {
  const content = normalizeSocketContent(payload.content);
  const topic = String(content?.topic ?? "").trim();
  if (topic) return topic;

  const phone = String(payload.phone ?? "").trim();
  if (phone) return phone;
  return String(payload.token ?? "").trim();
}

export function normalizeSocketContent(content: unknown): ChatSocketContent | null {
  if (!content || typeof content !== "object") return null;
  return content as ChatSocketContent;
}

export function formatDateTimeLabel(date: Date = new Date()): string {
  const datePart = date.toLocaleDateString("ru-RU");
  const timePart = date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart} ${timePart}`;
}

export function isOutgoingMessage(sender: string, userId: string): boolean {
  const normalizedSender = String(sender || "").trim().toLowerCase();
  const normalizedUserId = String(userId || "").trim().toLowerCase();
  if (!normalizedSender || !normalizedUserId) return false;
  if (normalizedSender === normalizedUserId) return true;
  if (normalizedSender.includes("client") || normalizedSender.includes("user")) return true;
  return false;
}

export function historyToViews(
  items: ChatHistoryMessage[],
  userId: string
): ChatMessageView[] {
  return items.map((item, index) => ({
    id: `history-${index}-${item.sender}-${item.text}`.slice(0, 120),
    text: String(item.text ?? ""),
    sender: String(item.sender ?? ""),
    isOutgoing: isOutgoingMessage(String(item.sender ?? ""), userId),
    timeLabel: "",
  }));
}

function parseCreatedAtTime(value: unknown): string {
  const iso = String(value ?? "").trim();
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return formatDateTimeLabel(date);
}

function historyDtoSender(item: ChatMessageDto): string {
  return String(item.senderType ?? item.sender ?? "")
    .trim()
    .toLowerCase();
}

function extractMessageText(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  return String(
    record.messageText ?? record.message_text ?? record.message ?? record.text ?? ""
  ).trim();
}

function historyDtoText(item: ChatMessageDto): string {
  return extractMessageText(item);
}

export function socketHistoryToViews(items: ChatMessageDto[], userId: string): ChatMessageView[] {
  return items
    .map((item, index) => {
      const sender = historyDtoSender(item);
      const text = historyDtoText(item);
      if (!text) return null;

      return {
        id: `ws-history-${item.id ?? index}`,
        text,
        sender: sender || "—",
        isOutgoing: isOutgoingMessage(sender, userId),
        timeLabel: parseCreatedAtTime(item.createdAt),
      };
    })
    .filter((item): item is ChatMessageView => Boolean(item));
}

export function socketPayloadToView(
  payload: ChatSocketPayload,
  userId: string,
  idSeed: string
): ChatMessageView | null {
  const messageType = String(payload.messageType ?? payload.messageTypeEnum ?? "")
    .trim()
    .toUpperCase();
  if (
    messageType &&
    !["TEXT", "MESSAGE", "SEND_MESSAGE_USER", "SEND_MESSAGE_OPERATOR"].includes(messageType)
  ) {
    return null;
  }

  const normalizedContent = normalizeSocketContent(payload.content);
  const content = normalizedContent
    ? extractMessageText(normalizedContent)
    : typeof payload.content === "string"
      ? String(payload.content).trim()
      : extractMessageText(payload);
  const rawSender = String(payload.sender ?? normalizedContent?.senderType ?? "").trim();
  const sender =
    rawSender.toLowerCase() === "system"
      ? String(normalizedContent?.senderType ?? "").trim() || rawSender
      : rawSender;
  const normalizedSender = sender.toLowerCase();
  if (!content) return null;

  const serverId = normalizedContent?.id ?? payload.id;
  const createdAt =
    String(normalizedContent?.createdAt ?? payload.createdAt ?? "").trim();

  return {
    id: serverId != null && String(serverId).trim() ? `ws-history-${serverId}` : idSeed,
    text: content,
    sender: normalizedSender || "—",
    isOutgoing: isOutgoingMessage(normalizedSender, userId),
    timeLabel: createdAt ? parseCreatedAtTime(createdAt) : formatDateTimeLabel(),
  };
}

export function extractOperatorRecipient(payload: ChatSocketPayload): string {
  const content = normalizeSocketContent(payload.content);
  const topic = String(content?.topic ?? "").trim();
  if (topic) return topic;

  const recipient = String(payload.recipient ?? "").trim();
  if (recipient) return recipient;

  const sender = String(payload.sender ?? "").trim().toLowerCase();
  const contentText = String(payload.content ?? "");
  if (sender.includes("operator") || sender === "system") {
    const match = contentText.match(/(?:назначен\s+оператор\s*:\s*)(\d{10,15})/i);
    if (match?.[1]) return match[1];
  }

  return "";
}
